import React, { useEffect, useMemo, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import Bottle, { BOTTLE } from './Bottle';
import Network from './Network';
import {
  CAMERA_HOME,
  STUDIO_BACKGROUND,
  StudioEnvironment,
  StudioLights,
  Sweep,
  configureRenderer,
} from './studio';
import {
  createSceneState,
  lerp,
  smoothstep,
  type SceneState,
  type StoryTimeline,
} from './state';

interface StorySceneProps {
  timeline: React.MutableRefObject<StoryTimeline>;
  reducedMotion: boolean;
}

interface RigProps extends StorySceneProps {
  state: React.MutableRefObject<SceneState>;
  stage: React.RefObject<THREE.Group | null>;
  bottle: React.RefObject<THREE.Group | null>;
}

/** Where the bottle sits across the screen once the copy column is in: 0 = centre, 1 = right edge. */
const BOTTLE_SCREEN_X = 0.46;
/** How far above centre the bottle sits on stacked layouts, as a fraction of the visible half-height. */
const BOTTLE_LIFT = 0.25;
/** Extra camera distance on stacked layouts once Act I is in, so the bottle sits above the copy. */
const MOBILE_ACT_PULLBACK = 2.6;
/** Pixel width below which the copy stacks under the bottle instead of beside it. */
const NARROW_PX = 960;

/** Reads the scroll timeline every frame and moves the camera and bottle. */
const Rig: React.FC<RigProps> = ({ timeline, reducedMotion, state, stage, bottle }) => {
  const camera = useThree((s) => s.camera);
  const size = useThree((s) => s.size);
  const target = useMemo(() => new THREE.Vector3(), []);
  const halfFov = Math.tan(THREE.MathUtils.degToRad(CAMERA_HOME.fov / 2));

  useFrame(({ clock }, delta) => {
    const sections = timeline.current;
    const goal: SceneState = {
      shift: smoothstep(sections[1].enter, 0.05, 0.8),
      signals: sections[2].enter,
      connections: sections[3].enter,
      focus: sections[4].enter,
      outro: smoothstep(sections[5].enter, 0, 0.9),
    };

    const current = state.current;
    const ease = reducedMotion ? 1 : 1 - Math.exp(-delta * 9);
    current.shift = lerp(current.shift, goal.shift, ease);
    current.signals = lerp(current.signals, goal.signals, ease);
    current.connections = lerp(current.connections, goal.connections, ease);
    current.focus = lerp(current.focus, goal.focus, ease);
    current.outro = lerp(current.outro, goal.outro, ease);

    const narrow = size.width < NARROW_PX;
    const aspect = size.width / Math.max(size.height, 1);

    // Portrait screens back the camera off so the bottle keeps a sensible width.
    // On mobile, ease back once more from the hero into Act I, then hold that frame.
    const portrait = Math.max(1, Math.sqrt(1.15 / aspect));
    const pullback = narrow ? MOBILE_ACT_PULLBACK * current.shift : 0;
    const distance = (CAMERA_HOME.position.z + pullback) * portrait;
    const halfWidth = distance * halfFov * aspect;
    const halfHeight = distance * halfFov;

    // Park the bottle at a fixed fraction of the screen so it always clears the copy.
    // How We Work: slide the whole stage off the right edge instead of dropping it.
    const apparentX = narrow ? 0 : halfWidth * BOTTLE_SCREEN_X * current.shift;
    const lift = narrow ? halfHeight * BOTTLE_LIFT * current.shift : 0;
    const exit = current.outro * (halfWidth * 2.4 + 2.8);
    const bottleX = apparentX * 2 + exit;

    if (stage.current) {
      stage.current.position.x = bottleX;
      stage.current.rotation.y = reducedMotion ? 0 : Math.sin(clock.elapsedTime * 0.17) * 0.05;
    }
    if (bottle.current) {
      bottle.current.rotation.y = reducedMotion ? 0 : Math.sin(clock.elapsedTime * 0.28) * 0.28;
    }

    // Hero: keep the bottle on the vertical centre. Act I slides it sideways on
    // wide screens and lifts it on stacked layouts so it sits above the copy.
    const navHeight =
      typeof document === 'undefined'
        ? 0
        : (document.querySelector('.consulting-navbar')?.getBoundingClientRect().height ?? 0);
    const belowNav = (navHeight / Math.max(size.height, 1)) * halfHeight;
    const heroY = BOTTLE.centerY + belowNav;
    const framedY = heroY - lift;

    const cameraAbove = CAMERA_HOME.position.y - heroY;
    camera.position.set(CAMERA_HOME.position.x + apparentX, framedY + cameraAbove, distance);
    target.set(apparentX, framedY, 0);
    camera.lookAt(target);
  }, -1);

  return null;
};

/** With reduced motion the loop runs on demand, so redraw when the page scrolls. */
const ScrollInvalidator: React.FC<{ enabled: boolean }> = ({ enabled }) => {
  const invalidate = useThree((s) => s.invalidate);

  useEffect(() => {
    if (!enabled) return undefined;
    const redraw = () => invalidate();
    window.addEventListener('scroll', redraw, { passive: true });
    window.addEventListener('resize', redraw);
    return () => {
      window.removeEventListener('scroll', redraw);
      window.removeEventListener('resize', redraw);
    };
  }, [enabled, invalidate]);

  return null;
};

const StoryScene: React.FC<StorySceneProps> = ({ timeline, reducedMotion }) => {
  const state = useRef<SceneState>(createSceneState());
  const stage = useRef<THREE.Group>(null);
  const bottle = useRef<THREE.Group>(null);

  return (
    <Canvas
      className="story-canvas"
      camera={{ position: CAMERA_HOME.position.toArray(), fov: CAMERA_HOME.fov, near: 0.1, far: 60 }}
      dpr={[1, 2]}
      shadows="variance"
      frameloop={reducedMotion ? 'demand' : 'always'}
      gl={{ antialias: true, alpha: false, powerPreference: 'high-performance' }}
      onCreated={({ gl }) => configureRenderer(gl)}
    >
      <color attach="background" args={[STUDIO_BACKGROUND]} />
      <StudioEnvironment />
      <StudioLights />
      <Sweep />
      <group ref={stage}>
        <group ref={bottle}>
          <Bottle />
        </group>
        <Network state={state} reducedMotion={reducedMotion} />
      </group>
      <Rig timeline={timeline} reducedMotion={reducedMotion} state={state} stage={stage} bottle={bottle} />
      <ScrollInvalidator enabled={reducedMotion} />
    </Canvas>
  );
};

export default StoryScene;
